import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { filter, find, map, uniq } from 'lodash';
import { Fragment } from 'react';
import { Field, FormSection, WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ReactSelect from 'dashboard/components/ReactSelect';
import ComparisonMethodsSection from 'dashboard/components-lib/MatchRequirementPanel/ComparisonMethodSection';
import NormalizationMethodsSection from 'dashboard/components-lib/MatchRequirementPanel/NormalizationMethodSection';
import {
  MatchRequirementKeys,
  matchRequirementMatchLevelName,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import {
  ComparisonMethod,
  MatchGroup,
  MatchRequirement,
  NormalizationMethod,
} from 'dashboard/models/verification-check';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label } from 'design-system/components/Text';
import { withMessage } from 'lib/utils/formValidation';

const PANEL_HEADER_HEIGHT = 76;
const PANEL_FOOTER_HEIGHT = 64;

const COMPARISON_METHOD_VALIDATION_MESSAGE = 'Invalid comparison method type';
const NORMALIZATION_METHOD_VALIDATION_MESSAGE = 'Invalid normalization method type';
const ATTRIBUTE_VALIDATION_MESSAGE = 'Attribute must be selected';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  height: ${PANEL_HEADER_HEIGHT}px;
  padding: ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_HEADER_HEIGHT + PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  padding: ${Spacing[5]};
  padding-bottom: ${Spacing[6]};
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const InputLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const validate = {
  'attribute-selector': [
    withMessage(
      ATTRIBUTE_VALIDATION_MESSAGE,
      (matchRequirement: MatchRequirement) => matchRequirement?.description != null,
    ),
  ],
  'comparison-methods': [
    withMessage(
      COMPARISON_METHOD_VALIDATION_MESSAGE,
      (comparisonMethods: ComparisonMethod[]) =>
        !comparisonMethods?.some((comparisonMethod) => comparisonMethod.type == null),
    ),
  ],
  'normalization-methods': [
    withMessage(
      NORMALIZATION_METHOD_VALIDATION_MESSAGE,
      (normalizationMethods: NormalizationMethod[]) =>
        !normalizationMethods?.some((normalizationMethod) => normalizationMethod.type == null),
    ),
  ],
};

interface MatchRequirementPanelProps {
  usesMatchGroups: boolean;
  matchRequirementProfiles: MatchRequirementProfile[];
  matchRequirementKeys: MatchRequirementKeys | null;
  matchRequirement: MatchRequirement;
  matchRequirementName: string;
  formSectionName: string;
  editable: boolean;
  handleClose: () => void;
}

const MatchRequirementPanel = ({
  usesMatchGroups,
  matchRequirementProfiles,
  matchRequirement,
  matchRequirementKeys,
  matchRequirementName,
  formSectionName,
  editable,
  handleClose,
}: MatchRequirementPanelProps) => {
  if (!matchRequirement || !matchRequirementKeys) return null;
  const {
    'match-group-index': matchGroupIndex,
    'match-requirement-index': matchRequirementIndex,
    scope: matchRequirementScope,
  } = matchRequirementKeys;

  return (
    <FormSection name={formSectionName} component={Fragment}>
      <Container>
        <HeaderContainer>
          <Heading size='sm'>Match Configuration</Heading>
        </HeaderContainer>
        <BodyContainer>
          <FieldWrapper>
            <Field
              id={`match-requirement-panel__${matchRequirementName}`}
              name={matchRequirementName}
              component={MatchRequirementAttributeSelector}
              editable={editable}
              matchRequirementProfiles={filter(matchRequirementProfiles, {
                scope: matchRequirementScope,
              })}
              validate={validate['attribute-selector']}
            />
          </FieldWrapper>
          {matchRequirement.description && (
            <FormSection name={matchRequirementName} component={Fragment}>
              <Field
                id='normalization-methods'
                name='normalization-methods'
                component={NormalizationMethodsSection}
                validate={validate['normalization-methods']}
                editable={editable}
              />
              <Field
                id='comparison-methods'
                name='comparison-methods'
                component={ComparisonMethodsSection}
                validate={validate['comparison-methods']}
                editable={editable}
              />
            </FormSection>
          )}
        </BodyContainer>
        <FooterContainer>
          <HStack equal>
            <Button
              variant='basic'
              id='match-requirement-panel__close-button'
              onClick={handleClose}
            >
              Close
            </Button>
            {editable &&
              (usesMatchGroups ? (
                <Field
                  id='match-requirement-panel__delete-button'
                  name='match-groups'
                  component={DeleteMatchRequirementButton}
                  editable={editable}
                  matchGroupIndex={matchGroupIndex}
                  matchRequirementIndex={matchRequirementIndex}
                  handleClose={handleClose}
                />
              ) : (
                <Field
                  id='match-requirement-panel__delete-button'
                  name='check-requirements'
                  component={DeleteCheckRequirementButton}
                  editable={editable}
                  checkRequirementIndex={matchRequirementIndex}
                  handleClose={handleClose}
                />
              ))}
          </HStack>
        </FooterContainer>
      </Container>
    </FormSection>
  );
};

interface MatchRequirementAttributeSelectorProps {
  matchRequirementProfiles: MatchRequirementProfile[];
  editable: boolean;
}

/**
 * Returns dropdowns to change the attribute or match level of a match requirement
 *
 * @param matchRequirementProfiles - All possible match requirement profiles for the object
 * @param input - Stores the match requirement object passed through the redux form
 *
 * @returns {MatchRequirementRow}
 */
const MatchRequirementAttributeSelector = ({
  matchRequirementProfiles,
  editable,
  input,
}: MatchRequirementAttributeSelectorProps & WrappedFieldProps) => {
  const matchRequirement = input.value as MatchRequirement;
  const attributeName = matchRequirement.description;
  const matchLevelName = matchRequirementMatchLevelName(
    matchRequirement,
    matchRequirementProfiles,
    //max match requirement blurbs
    2,
  );
  const attributeNames = uniq(map(matchRequirementProfiles, 'attribute-name'));
  const matchLevelNames = map(
    filter(matchRequirementProfiles, { 'attribute-name': attributeName }),
    'match-level-name',
  );

  /**
   * Updates the match requirement to a corresponding match requirement profile of the provided attribute
   */
  const handleAttributeSelect = (attribute: string) => {
    if (!attribute) return;
    input.onChange(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      find(matchRequirementProfiles, { 'attribute-name': attribute })['match-requirement'],
    );
  };
  /**
   * Updates the match requirement to the corresponding match requirement profile of the provided matchLevel
   */
  const handleMatchLevelSelect = (matchLevel: string) => {
    if (!matchLevel) return;
    input.onChange(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      find(matchRequirementProfiles, {
        'attribute-name': attributeName,
        'match-level-name': matchLevel,
      })['match-requirement'],
    );
  };

  return (
    <>
      <InputLabel>Attribute</InputLabel>
      <Spacer height={1} />
      {/* TODO(PDS-360): Replace with design-system/components/Select */}
      <ReactSelect
        value={attributeName}
        placeholder={attributeName || 'None'}
        onChange={handleAttributeSelect}
        options={attributeNames.map((attribute: string) => ({
          label: attribute,
          value: attribute,
        }))}
        disabled={!editable}
      />
      <Spacer height={2} />
      <InputLabel>Match Level</InputLabel>
      <Spacer height={1} />
      <ReactSelect
        value={matchLevelName}
        placeholder={matchLevelName || 'None'}
        onChange={handleMatchLevelSelect}
        options={matchLevelNames.map((matchLevel: string) => ({
          label: matchLevel,
          value: matchLevel,
        }))}
        disabled={!editable || attributeName == null}
      />
      <Spacer height={2} />
    </>
  );
};

interface DeleteMatchRequirementButtonProps {
  matchGroupIndex: number;
  matchRequirementIndex: number;
  editable: boolean;
  handleClose: () => void;
}

const DeleteMatchRequirementButton = ({
  matchGroupIndex,
  matchRequirementIndex,
  editable,
  input,
  handleClose,
}: DeleteMatchRequirementButtonProps & WrappedFieldProps) => {
  const handleDelete = () => {
    const matchGroups = produce(input.value, (draftMatchGroups: MatchGroup[]) => {
      draftMatchGroups[matchGroupIndex]['match-requirements'].splice(matchRequirementIndex, 1);
      if (draftMatchGroups[matchGroupIndex]['match-requirements'].length === 0)
        draftMatchGroups.splice(matchGroupIndex, 1);
    });
    input.onChange(matchGroups);
    handleClose();
  };
  return (
    <Button
      variant='danger'
      id='match-requirement-panel__delete-button'
      disabled={!editable}
      onClick={handleDelete}
    >
      Delete
    </Button>
  );
};

// TODO: delete after match groups are fully implemented
interface DeleteCheckRequirementButtonProps {
  checkRequirementIndex: number;
  editable: boolean;
  handleClose: () => void;
}

const DeleteCheckRequirementButton = ({
  checkRequirementIndex,
  editable,
  input,
  handleClose,
}: DeleteCheckRequirementButtonProps & WrappedFieldProps) => {
  const handleDelete = () => {
    const checkRequirements = produce(input.value, (draftMatchRequirements: MatchRequirement[]) => {
      draftMatchRequirements.splice(checkRequirementIndex, 1);
    });
    input.onChange(checkRequirements);
    handleClose();
  };
  return (
    <Button
      variant='danger'
      id='match-requirement-panel__delete-button'
      disabled={!editable}
      onClick={handleDelete}
    >
      Delete
    </Button>
  );
};

export default MatchRequirementPanel;
